import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell, Legend } from 'recharts';
import { AGING_BUCKETS } from '@/hooks/useDealAging';

const STAGE_CONFIG = {
  new: { label: 'Novo', color: 'hsl(var(--chart-1))' },
  contact: { label: 'Contato', color: 'hsl(var(--chart-2))' },
  scheduled: { label: 'Reunião Agendada', color: 'hsl(var(--chart-3))' },
  proposal: { label: 'Proposta', color: 'hsl(var(--chart-4))' },
  negotiation: { label: 'Negociação', color: 'hsl(var(--chart-5))' },
};

const STAGE_ORDER = ['new', 'contact', 'scheduled', 'proposal', 'negotiation'];

export default function DealAgingChart({ bucketData, onBucketClick }) {
  // Transform data for horizontal stacked bar chart
  const chartData = useMemo(() => {
    return bucketData.map(bucket => ({
      name: bucket.label,
      key: bucket.key,
      total: bucket.total,
      deals: bucket.deals,
      ...bucket.stageCounts,
    }));
  }, [bucketData]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    
    const data = payload[0]?.payload;
    if (!data) return null;

    return (
      <div className="bg-popover border rounded-lg shadow-lg p-3 text-sm">
        <p className="font-semibold mb-2">{label}</p>
        <p className="text-muted-foreground mb-2">Total: {data.total} deals</p>
        <div className="space-y-1">
          {STAGE_ORDER.map(stage => {
            const count = data[stage];
            if (!count) return null;
            const cfg = STAGE_CONFIG[stage] ?? { label: stage, color: 'hsl(var(--muted))' };
            return (
              <div key={stage} className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-sm" 
                  style={{ backgroundColor: cfg.color }}
                />
                <span>{cfg.label}: {count}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const handleBarClick = (data) => {
    if (!data || !onBucketClick) return;
    const bucket = bucketData.find(b => b.label === data.name);
    if (bucket) {
      onBucketClick(bucket.deals, bucket.label);
    }
  };

  if (!chartData.length) {
    return (
      <div className="h-[280px] flex items-center justify-center text-muted-foreground">
        No active deals to display
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ResponsiveContainer width="100%" height={280}>
        <BarChart 
          data={chartData} 
          layout="vertical"
          margin={{ top: 5, right: 60, left: 80, bottom: 5 }}
          onClick={(e) => e?.activePayload?.[0]?.payload && handleBarClick(e.activePayload[0].payload)}
        >
          <XAxis type="number" tickLine={false} axisLine={false} fontSize={12} />
          <YAxis 
            type="category" 
            dataKey="name" 
            tickLine={false} 
            axisLine={false}
            fontSize={12}
            width={75}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'hsl(var(--muted) / 0.3)' }} />
          
          {STAGE_ORDER.map((stage, idx) => {
            const cfg = STAGE_CONFIG[stage] ?? { color: 'hsl(var(--muted))' };
            const isLast = idx === STAGE_ORDER.length - 1;
            return (
              <Bar
                key={stage}
                dataKey={stage}
                stackId="stages"
                fill={cfg.color}
                cursor="pointer"
                radius={isLast ? [0, 4, 4, 0] : 0}
              />
            );
          })}
        </BarChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="flex flex-wrap justify-center gap-4 text-xs">
        {STAGE_ORDER.map(stage => {
          const cfg = STAGE_CONFIG[stage] ?? { label: stage, color: 'hsl(var(--muted))' };
          return (
            <div key={stage} className="flex items-center gap-1.5">
              <div 
                className="w-3 h-3 rounded-sm" 
                style={{ backgroundColor: cfg.color }}
              />
              <span className="text-muted-foreground">{cfg.label}</span>
            </div>
          );
        })}
      </div>

      {/* Urgency indicator */}
      <div className="flex justify-center gap-6 text-xs pt-2 border-t">
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'hsl(0, 70%, 40%)' }} />
          <span className="text-muted-foreground">Crítico</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'hsl(30, 90%, 50%)' }} />
          <span className="text-muted-foreground">Alerta</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'hsl(140, 70%, 40%)' }} />
          <span className="text-muted-foreground">OK</span>
        </div>
      </div>
    </div>
  );
}
